import { isFunction } from 'lodash';
import { useCallback, useMemo } from 'react';

import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { assertUnreachable, Bean, FieldControllerProps, useTranslate, ValidationRules } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { personalDataTranslationKeys } from '../../../translations';

const GUARDIAN_EXTERNAL_IDENTIFIER_TYPES = [Pesel.CLASS, Passport.CLASS, NationalIdentificationNumber.CLASS];

const SATURATORY_REPRESENTATIVE_GUARDIAN_EXTERNAL_IDENTIFIER_TYPES = [Pesel.CLASS];

export type GuardianExternalIdentifierType = typeof GUARDIAN_EXTERNAL_IDENTIFIER_TYPES[number];
export type SaturatoryRepresentativeGuardianExternalIdentifierType = typeof SATURATORY_REPRESENTATIVE_GUARDIAN_EXTERNAL_IDENTIFIER_TYPES[number];

export type GuardianExternalIdentifierTypeVisitor = <ACCEPT>(
   visitor:
      | ((type: GuardianExternalIdentifierType) => ACCEPT)
      | Record<`visit${GuardianExternalIdentifierType}`, () => ACCEPT>
) => ACCEPT;

export const createGuardianExternalIdentifierTypeVisitor = (
   guardianExternalIdentifierType: GuardianExternalIdentifierType
): GuardianExternalIdentifierTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(guardianExternalIdentifierType);
   }

   switch (guardianExternalIdentifierType) {
      case 'Pesel':
         return visitor.visitPesel();
      case 'Passport':
         return visitor.visitPassport();
      case 'NationalIdentificationNumber':
         return visitor.visitNationalIdentificationNumber();
      default:
         return assertUnreachable(guardianExternalIdentifierType);
   }
};

const VALIDATION_RULES: ValidationRules<Bean> = {
   required: true,
};

const useToggleLabelExtractor = (): ((type?: GuardianExternalIdentifierType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(personalDataTranslationKeys.identifierType.typeName[type]) : '';
      },
      [translate]
   );
};

const useSelectItems = (statutoryRepresentative: boolean): GuardianExternalIdentifierType[] => {
   return useMemo(() => {
      return statutoryRepresentative
         ? SATURATORY_REPRESENTATIVE_GUARDIAN_EXTERNAL_IDENTIFIER_TYPES
         : GUARDIAN_EXTERNAL_IDENTIFIER_TYPES;
   }, [statutoryRepresentative]);
};

export type OnGuardianExternalIdentifierTypeSelect = (type: GuardianExternalIdentifierType | null) => void;

type GuardianExternalIdentifierTypeFieldProps = FieldControllerProps<
   'guardianExternalIdentifierType',
   GuardianExternalIdentifierType
> & {
   readonly statutoryRepresentative: boolean;
   readonly onSelect: OnGuardianExternalIdentifierTypeSelect;
};

export const GuardianExternalIdentifierTypeField = (props: GuardianExternalIdentifierTypeFieldProps): JSX.Element => {
   const statutoryRepresentativeTypes = useSelectItems(props.statutoryRepresentative);
   return (
      <Select<GuardianExternalIdentifierType>
         testId={'guardianExternalIdentifierType'}
         label={personalDataTranslationKeys.identifierType.label}
         triggerLabelExtractor={useToggleLabelExtractor()}
         controller={props.register('guardianExternalIdentifierType', VALIDATION_RULES)}
         defaultValue={'Pesel'}
         onSelect={props.onSelect}
      >
         <Select.List<GuardianExternalIdentifierType> items={statutoryRepresentativeTypes}>
            {(statutoryRepresentativeType: GuardianExternalIdentifierType) => (
               <Select.Item
                  item={statutoryRepresentativeType}
                  testId={statutoryRepresentativeType}
                  label={personalDataTranslationKeys.identifierType.typeName[statutoryRepresentativeType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
